'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import Flag from 'react-flagkit'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { SaveIcon, XIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { setValidationErrors, getErrorMessage } from '@/lib/utils'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Editor } from '@/components/ui/editor'
import { useContentSettings, useUpdateContentSettings, useUnsavedChangesGuard } from '@/hooks'
import { useContentSettingsStore, useMetaStore } from '@/store'
import { ContentSettings } from '@/types/meta'
import { UnsavedChangesGuardDialog } from '@/components/partials'

const CONTRACT_FIELDS = [
  'termsOfUseContract',
  'confidentialityAgreementContract',
  'cookiePolicyContract',
  'pdplContract',
  'distanceSellingContract'
] as const

type ContractField = (typeof CONTRACT_FIELDS)[number]

type ContentSettingsFormData = {
  title: Record<string, string>
  description: Record<string, string>
  keywords: Record<string, string>
  termsOfUseContract: Record<string, string>
  confidentialityAgreementContract: Record<string, string>
  cookiePolicyContract: Record<string, string>
  pdplContract: Record<string, string>
  distanceSellingContract: Record<string, string>
}

const ContentSettingsForm = () => {
  const t = useTranslations()
  const queryClient = useQueryClient()
  const { metaState } = useMetaStore()

  const { isLoading: isLoadingSettings, data: settingsData, error: settingsError } = useContentSettings()
  const { mutate: updateMutation, isPending: isUpdating } = useUpdateContentSettings()

  const localesData = useMemo(() => metaState.response?.locales, [metaState.response])
  const locales = useMemo(() => localesData || [], [localesData])

  const getCountryCodeFromLocale = (localeCode: string): string => {
    const parts = localeCode.split('-')
    return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
  }

  const hasLocaleError = (localeCode: string): boolean => {
    return !!(
      formErrors.title?.[localeCode] ||
      formErrors.description?.[localeCode] ||
      formErrors.keywords?.[localeCode] ||
      CONTRACT_FIELDS.some(field => formErrors[field]?.[localeCode])
    )
  }

  const schema = useMemo(() => {
    const titleSchema: Record<string, z.ZodString> = {}
    const descriptionSchema: Record<string, z.ZodString> = {}
    const keywordsSchema: Record<string, z.ZodOptional<z.ZodString>> = {}
    const contractSchema: Record<string, z.ZodOptional<z.ZodString>> = {}

    locales.forEach(locale => {
      titleSchema[locale.fullCode] = z.string().min(1, t('required'))
      descriptionSchema[locale.fullCode] = z.string().min(1, t('required'))
      keywordsSchema[locale.fullCode] = z.string().optional()
      contractSchema[locale.fullCode] = z.string().optional()
    })

    const contractFieldSchema = z.object(contractSchema)

    return z.object({
      title: z.object(titleSchema),
      description: z.object(descriptionSchema),
      keywords: z.object(keywordsSchema),
      termsOfUseContract: contractFieldSchema,
      confidentialityAgreementContract: contractFieldSchema,
      cookiePolicyContract: contractFieldSchema,
      pdplContract: contractFieldSchema,
      distanceSellingContract: contractFieldSchema
    })
  }, [locales, t])

  const getDefaultValues = useCallback((): ContentSettingsFormData => {
    const defaultTitle: Record<string, string> = {}
    const defaultDescription: Record<string, string> = {}
    const defaultKeywords: Record<string, string> = {}
    const defaults: Record<ContractField, Record<string, string>> = {
      termsOfUseContract: {},
      confidentialityAgreementContract: {},
      cookiePolicyContract: {},
      pdplContract: {},
      distanceSellingContract: {}
    }

    locales.forEach(locale => {
      defaultTitle[locale.fullCode] = settingsData?.title?.[locale.fullCode] ?? ''
      defaultDescription[locale.fullCode] = settingsData?.description?.[locale.fullCode] ?? ''
      defaultKeywords[locale.fullCode] = settingsData?.keywords?.[locale.fullCode] ?? ''
      CONTRACT_FIELDS.forEach(field => {
        defaults[field][locale.fullCode] = settingsData?.[field]?.[locale.fullCode] ?? ''
      })
    })

    return {
      title: defaultTitle,
      description: defaultDescription,
      keywords: defaultKeywords,
      ...defaults
    }
  }, [locales, settingsData])

  type SchemaType = z.infer<typeof schema>

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues() as SchemaType,
    mode: 'onChange'
  })

  const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

  useEffect(() => {
    if (locales.length > 0) {
      const newDefaultValues = getDefaultValues()
      form.reset(newDefaultValues)
    }
  }, [getDefaultValues, locales, form])

  useEffect(() => {
    if (settingsError) {
      const message = getErrorMessage(settingsError, t('something_went_wrong'))
      toast.error(message)
    }
  }, [settingsError, t])

  const formErrors = form.formState.errors

  const handleSubmit = async (data: SchemaType) => {
    const transformedData: ContentSettings = {
      title: data.title,
      description: data.description,
      keywords: {},
      termsOfUseContract: {},
      confidentialityAgreementContract: {},
      cookiePolicyContract: {},
      pdplContract: {},
      distanceSellingContract: {}
    }

    locales.forEach(locale => {
      transformedData.keywords[locale.fullCode] = data.keywords[locale.fullCode] || ''
      CONTRACT_FIELDS.forEach(field => {
        transformedData[field][locale.fullCode] = (data[field] as Record<string, string>)[locale.fullCode] || ''
      })
    })

    updateMutation(transformedData, {
      onSuccess: async (updatedSettings: ContentSettings) => {
        queryClient.setQueryData(['contentSettings'], updatedSettings)
        useContentSettingsStore.setState({
          contentSettingsState: {
            isLoading: false,
            response: updatedSettings,
            error: null
          }
        })
        const { getMeta } = useMetaStore.getState()
        await getMeta()
        toast.success(t('settings_updated_successfully'))
      },
      onError: (error: any) => {
        const message = getErrorMessage(error, t('something_went_wrong'))
        if (error.response?.status === 422) {
          setValidationErrors(error, form.setError)
        } else {
          toast.error(message)
        }
      }
    })
  }

  const handleCancel = () => {
    if (locales.length > 0) {
      const newDefaultValues = getDefaultValues()
      form.reset(newDefaultValues)
    }
  }

  const isPageLoading = !metaState.response?.locales || (isLoadingSettings && !settingsData)

  if (isPageLoading) {
    const tabCount = metaState.response?.locales?.length ?? 2
    return (
      <div className="space-y-6">
        {/* Locale tabs */}
        <div className="flex gap-1">
          {Array.from({ length: tabCount }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-24 rounded-md" />
          ))}
        </div>

        {/* title */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10 w-full" />
        </div>

        {/* description */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-20 w-full" />
        </div>

        {/* keywords */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-full" />
        </div>

        {/* 5 contract WYSIWYG fields */}
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-63 w-full rounded-md" />
          </div>
        ))}

        {/* actions */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {locales.length > 0 ? (
          <Tabs
            defaultValue={locales[0].fullCode}
            className="w-full"
            onValueChange={value => {
              setTimeout(() => {
                const input = document.querySelector(`[name="title.${value}"]`) as HTMLInputElement
                if (input) {
                  input.focus()
                }
              }, 100)
            }}>
            <TabsList className="min-h-10.5 w-full">
              {locales.map(locale => {
                const hasError = hasLocaleError(locale.fullCode)
                return (
                  <TabsTrigger
                    key={locale.fullCode}
                    value={locale.fullCode}
                    className={`relative flex items-center gap-2 ${
                      hasError ? 'text-destructive border-destructive' : ''
                    }`}>
                    <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                    <span className="hidden sm:inline">{locale.name}</span>
                    {hasError && (
                      <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                    )}
                  </TabsTrigger>
                )
              })}
            </TabsList>

            {locales.map(locale => (
              <TabsContent key={locale.fullCode} value={locale.fullCode} className="mt-6 space-y-4">
                <div className="mb-4 flex items-center gap-2">
                  <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                  <h4 className="text-lg font-semibold">{locale.name}</h4>
                  <Badge variant="outline" className="text-xs">
                    {locale.fullCode}
                  </Badge>
                  {hasLocaleError(locale.fullCode) && (
                    <Badge variant="destructive" className="animate-pulse text-xs">
                      {t('error')}
                    </Badge>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name={`title.${locale.fullCode}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('title')}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={t('title')} disabled={isUpdating} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`description.${locale.fullCode}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('description')}</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder={t('description')} disabled={isUpdating} rows={5} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`keywords.${locale.fullCode}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('keywords')}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={t('keywords')} disabled={isUpdating} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {CONTRACT_FIELDS.map(contractField => (
                  <FormField
                    key={contractField}
                    control={form.control}
                    name={`${contractField}.${locale.fullCode}`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t(
                            `${contractField
                              .replace(/([A-Z])/g, '_$1')
                              .toLowerCase()
                              .replace(/^_/, '')}`
                          )}
                        </FormLabel>
                        <FormControl>
                          <Editor
                            placeholder={t(
                              `${contractField
                                .replace(/([A-Z])/g, '_$1')
                                .toLowerCase()
                                .replace(/^_/, '')}`
                            )}
                            value={field.value || ''}
                            autoFocus={false}
                            onChange={html => field.onChange(html)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <Alert>
            <AlertTitle>{t('no_locales_available')}</AlertTitle>
            <AlertDescription>{t('please_add_locales_first')}</AlertDescription>
          </Alert>
        )}

        <Separator />

        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isUpdating}>
            <XIcon className="h-4 w-4" />
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={isUpdating} loading={isUpdating}>
            <SaveIcon className="h-4 w-4" />
            {t('save')}
          </Button>
        </div>
        <UnsavedChangesGuardDialog
          open={isNavigationBlocked}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </form>
    </Form>
  )
}

export default ContentSettingsForm
